import { Badge, Dropdown, Empty } from 'antd';
import IntlMessages from '@nxsys/helpers/IntlMessages';
import NotificationItem from './NotificationItem';
import Icon from '@nxsys/components/AppIconImage';
import { IconBell } from '@nxsys/assets/icons';
import {
  StyledDrowdownWrapper,
  StyledNotifyButtonAll,
  StyledNotifyIcon,
  StyledNotifyLink,
  StyledNotifyList,
  StyledNotifyScrollSubmenu,
  StyledNotifyText,
} from './index.styled';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nxsys/store';
import { notificationActions } from '@nxsys/store/slices/notification/reducer/notificationSlice';
import { getNotificationRoute } from './notificationRoutes';
import { IRELAND } from '@nxsys/store/constants/keys';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';

const AppNotifications = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigateWithQuery();
  const { data: notifications = [], totalUnread = 0 } = useSelector(
    (state: AppState) => state.notificationStore?.notificationList || {},
  );
  const { user, isEmployee, isAgency, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );

  const handleDropdownOpenChange = (open: boolean) => {
    if (open && totalUnread > 0) {
      dispatch(
        notificationActions.markAllReadNotification({
          role: (user as any)?.role,
          userId: (user as any)?.id,
          subcontractorId: (user as any)?.subcontractorId?.id,
          agencyId: (user as any)?.agencyId?.id,
        }),
      );
    }
  };

  const handleNotificationClick = (item: any) => {
    dispatch(notificationActions.markReadNotification({ id: item.id }));
    const route = getNotificationRoute(item, {
      isEmployee,
      isAgency,
      isIreland: payrollCountry === IRELAND,
    });
    if (route) navigate(route);
  };

  const items = [
    {
      key: 1,
      label: (
        <span className="header">
          <IntlMessages id="common.notifications" />
          {totalUnread > 0 ? ` (${totalUnread})` : ''}
        </span>
      ),
    },
    {
      key: 2,
      label: (
        <StyledNotifyScrollSubmenu>
          {notifications.length > 0 ? (
            <StyledNotifyList
              dataSource={notifications.slice(0, 20)}
              renderItem={(item: any) => (
                <NotificationItem
                  key={item.id}
                  item={item}
                  onClick={() => handleNotificationClick(item)}
                />
              )}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No notifications"
              style={{ padding: '20px 0' }}
            />
          )}
        </StyledNotifyScrollSubmenu>
      ),
    },
    {
      key: 3,
      label: (
        <StyledNotifyButtonAll
          type="text"
          onClick={() => navigate('/notifications')}
        >
          <IntlMessages id="common.viewAll" />
        </StyledNotifyButtonAll>
      ),
    },
  ];

  return (
    <StyledDrowdownWrapper>
      <Dropdown
        menu={{ items }}
        className="dropdown"
        overlayClassName="header-notify-messages"
        getPopupContainer={(triggerNode) => triggerNode}
        trigger={['click']}
        onOpenChange={handleDropdownOpenChange}
      >
        <StyledNotifyLink onClick={(e) => e.preventDefault()}>
          <StyledNotifyIcon className={totalUnread > 0 ? 'header-icon-has-unread' : ''}>
            <Badge count={totalUnread} size="small" offset={[-2, 2]}>
              <Icon src={IconBell} alt="Notifications" />
            </Badge>
          </StyledNotifyIcon>
          <StyledNotifyText>
            <IntlMessages id="common.notifications" />
          </StyledNotifyText>
        </StyledNotifyLink>
      </Dropdown>
    </StyledDrowdownWrapper>
  );
};

export default AppNotifications;
